import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import {
  IconButton,
  InputAdornment,
  Tooltip
} from '@material-ui/core'
import {
  CancelOutlined,
  Search
} from '@material-ui/icons'
import TextField from '../components/TextField.js'
import { FOOTER_BACKGROUND } from '../styles.js'
import redux from '../redux/index.js'

const ENTER = ['Enter', 'NumpadEnter']
const ESCAPE = 'Escape'

const FileSearchField = ({
  selected,
  searchValue,
  showSearchResults,
  isSearching,
  strings,
  inFooter = false,
  footerExpanded = true,
  onFocusChange,
  onSearchValueChange,
  onStartSearch,
  onResetSearch
}) => {
  const hasSelectedContext = !!selected.current
  const fieldEnabled = selected.current && !isSearching
  const buttonEnabled = showSearchResults || !!searchValue
  const hasActiveSearch = !!searchValue || showSearchResults
  const searchTooltipIdle = strings.file_search_tooltip_idle || 'Search'
  const searchTooltipSearching = strings.file_search_tooltip_searching || 'Searching'
  const trimmedSearchValue = String(searchValue || '').trim()
  const collapsedTooltipTitle = trimmedSearchValue.length > 0
    ? `${searchTooltipSearching} "${trimmedSearchValue}"`
    : searchTooltipIdle
  const rootRef = useRef(null)
  const inputRef = useRef(null)
  const [focusPending, setFocusPending] = useState(false)

  const onChange = (event) => onSearchValueChange(event.target.value)

  const onKeyPress = (event) => {
    if (ENTER.includes(event.key) && searchValue.length >= 2) {
      onStartSearch(selected.current, searchValue)
    }
  }

  const onKeyDown = (event) => {
    if (event.key === ESCAPE && inFooter && footerExpanded && typeof onFocusChange === 'function') {
      event.preventDefault()
      event.stopPropagation()
      onFocusChange(false)
      if (inputRef.current && typeof inputRef.current.blur === 'function') {
        inputRef.current.blur()
      }
    }
  }

  const onButtonClick = () => {
    if (buttonEnabled) {
      onResetSearch()
      return
    }

    if (inFooter && footerExpanded && typeof onFocusChange === 'function') {
      onFocusChange(false)
    }
  }

  const onCollapsedClick = () => {
    if (!hasSelectedContext) {
      return
    }
    if (typeof onFocusChange === 'function') {
      onFocusChange(true)
    }
    setFocusPending(true)
  }

  useEffect(() => {
    if (!focusPending || !inFooter || !footerExpanded) {
      return
    }

    let attempts = 0
    const maxAttempts = 8
    let rafId = null

    const tryFocus = () => {
      if (inputRef.current) {
        inputRef.current.focus()
        setFocusPending(false)
        return
      }
      attempts += 1
      if (attempts < maxAttempts) {
        rafId = window.requestAnimationFrame(tryFocus)
      } else {
        setFocusPending(false)
      }
    }

    rafId = window.requestAnimationFrame(tryFocus)

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId)
      }
    }
  }, [focusPending, inFooter, footerExpanded])

  useEffect(() => {
    if (!inFooter || !footerExpanded || typeof onFocusChange !== 'function') {
      return
    }

    const onPointerDown = (event) => {
      const root = rootRef.current
      if (root && !root.contains(event.target)) {
        onFocusChange(false)
      }
    }

    document.addEventListener('mousedown', onPointerDown, true)
    document.addEventListener('touchstart', onPointerDown, true)

    return () => {
      document.removeEventListener('mousedown', onPointerDown, true)
      document.removeEventListener('touchstart', onPointerDown, true)
    }
  }, [inFooter, footerExpanded, onFocusChange])

  const inputHeight = 32
  const inputRadius = inFooter ? 7 : 6

  const searchInputProps = {
    style: {
      background: 'white',
      borderRadius: inputRadius,
      height: inputHeight,
      overflow: 'hidden'
    },
    endAdornment: (
      <InputAdornment position='end'>
        <CancelOutlined
          style={closeButtonStyle(buttonEnabled, inFooter)}
          onMouseDown={(event) => {
            // Keep focus on the input so blur-collapse doesn't swallow the clear click.
            event.preventDefault()
          }}
          onClick={onButtonClick}
        />
      </InputAdornment>
    ),
    inputProps: { autoCapitalize: 'off', autoComplete: 'off', autoCorrect: 'off', spellCheck: 'false' }
  }

  if (inFooter && !footerExpanded) {
    return (
      <span ref={rootRef} style={collapsedButtonsWrapperStyle}>
        <Tooltip title={collapsedTooltipTitle} arrow>
          <IconButton
            size='small'
            style={collapsedSearchButtonStyle(hasSelectedContext, hasActiveSearch)}
            onClick={onCollapsedClick}
            onFocus={onCollapsedClick}
            disabled={!hasSelectedContext}
          >
            <Search style={collapsedSearchIconStyle(hasSelectedContext, hasActiveSearch)} />
          </IconButton>
        </Tooltip>
        {hasActiveSearch
          ? (
            <Tooltip title={strings.file_search_clear || 'Clear Search'} arrow>
              <IconButton
                size='small'
                style={collapsedClearButtonStyle}
                onMouseDown={(event) => {
                  event.preventDefault()
                }}
                onClick={onResetSearch}
                aria-label={strings.file_search_clear || 'Clear Search'}
              >
                <CancelOutlined style={collapsedClearIconStyle} />
              </IconButton>
            </Tooltip>
            )
          : null}
      </span>
    )
  }

  return (
    <div ref={rootRef} style={inFooter ? footerSearchWrapperStyle : searchWrapperStyle}>
      <div style={searchRowStyle}>
        <TextField
          fullWidth
          value={searchValue}
          onChange={onChange}
          onKeyPress={onKeyPress}
          onKeyDown={onKeyDown}
          InputProps={searchInputProps}
          style={searchFieldStyle}
          disabled={!fieldEnabled}
          inputRef={inputRef}
          onFocus={() => {
            if (inFooter && typeof onFocusChange === 'function') {
              onFocusChange(true)
            }
          }}
          onBlur={() => {
            if (inFooter && typeof onFocusChange === 'function') {
              onFocusChange(false)
            }
          }}
        />
      </div>
    </div>
  )
}

const searchFieldStyle = {
  background: 'transparent',
  marginTop: 0,
  marginBottom: 0
}

const searchWrapperStyle = {
  marginLeft: 120,
  marginRight: 40
}

const footerSearchWrapperStyle = {
  width: '100%'
}

const searchRowStyle = {
  display: 'flex',
  alignItems: 'center',
  width: '100%'
}

const collapsedButtonsWrapperStyle = {
  display: 'inline-flex',
  alignItems: 'flex-start',
  gap: 0
}

const collapsedClearButtonStyle = {
  width: 22,
  height: 22,
  minWidth: 22,
  padding: 0,
  borderRadius: 6,
  background: '#ffffff',
  border: `1px solid ${FOOTER_BACKGROUND}`,
  color: FOOTER_BACKGROUND,
  marginLeft: -2
}

const collapsedClearIconStyle = {
  fontSize: 14,
  color: FOOTER_BACKGROUND
}

const collapsedSearchButtonStyle = (enabled, active) => {
  if (active) {
    return {
      width: 32,
      height: 32,
      padding: 4,
      borderRadius: 8,
      background: '#ffffff',
      border: '1px solid #d5dbe0',
      color: FOOTER_BACKGROUND
    }
  }

  return {
    width: 32,
    height: 32,
    padding: 4,
    color: enabled ? 'white' : '#aaafb2'
  }
}

const collapsedSearchIconStyle = (enabled, active) => {
  return {
    fontSize: 18,
    color: active ? FOOTER_BACKGROUND : (enabled ? 'white' : '#aaafb2')
  }
}

const closeButtonStyle = (enabled, inFooter) => {
  return {
    position: 'absolute',
    cursor: 'pointer',
    zIndex: 7,
    right: 0,
    bottom: 7,
    marginRight: 5,
    color: enabled ? '#000000' : '#888888'
  }
}

const mapStateToProps = (state) => {
  const {
    searchValue,
    showSearchResults,
    isSearching
  } = state.files
  return {
    strings: state.locale.strings,
    selected: state.selected,
    searchValue,
    showSearchResults,
    isSearching
  }
}

const mapDispatchToProps = (dispatch) => {
  const { files } = redux.actions
  return {
    onSearchValueChange: (value) => {
      dispatch(files.setSearchValue(value))
    },
    onStartSearch: (current, searchValue) => {
      dispatch(files.searchFiles(current, searchValue))
    },
    onResetSearch: () => {
      dispatch(files.resetSearch())
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(FileSearchField)
